import { CSSProperties, useMemo, useState } from 'react'
import { Banner, DataTable, Icon } from '@boltenergy-be/design-system'
import { DataTableRow } from '@boltenergy-be/design-system/dist/components/data-table/types'
import classNames from 'classnames'
import { NestedDataTableProps } from './types.ts'
import { toDataTableRow } from './utils.ts'
import styles from './NestedDataTable.module.scss'

const NestedDataTable = ({ columns, rows, toggleLabel = 'Toggle details', className }: NestedDataTableProps) => {
  // Local state
  const [expandedRowIds, setExpandedRowIds] = useState<string[]>([])

  // Memoized constants
  const columnWidths = useMemo<CSSProperties>(() => {
    const totalFraction = columns.reduce((total, { fraction = 1 }) => total + (fraction || 1), 0)

    return Object.fromEntries(
      columns.map(({ fraction = 1 }, index) => [
        `--column-${index + 1}`,
        `calc((100% - var(--expand-column-width)) * ${fraction || 1} / ${totalFraction})`
      ])
    )
  }, [columns])

  const dataTableRows = useMemo<DataTableRow<string>[]>(
    () =>
      rows.flatMap((row) => {
        const isExpanded = expandedRowIds.includes(row.id)
        const toggle = () => setExpandedRowIds((current) => (isExpanded ? current.filter((id) => id !== row.id) : [...current, row.id]))

        const parent = toDataTableRow(
          row,
          columns,
          row.children?.length && (
            <button type="button" className={styles.toggle} aria-label={toggleLabel} aria-expanded={isExpanded} onClick={toggle}>
              <Icon name={isExpanded ? 'chevronUp' : 'chevronDown'} size={12} />
            </button>
          )
        )

        return isExpanded && row.children?.length ? [parent, ...row.children.map((child) => toDataTableRow(child, columns, ''))] : [parent]
      }),
    [rows, columns, expandedRowIds, toggleLabel]
  )

  return dataTableRows.length ? (
    <div className={classNames(styles.scroller, className)} style={columnWidths}>
      <DataTable rows={dataTableRows} className={styles.table} />
    </div>
  ) : (
    <Banner type="neutral">No data found</Banner>
  )
}

export default NestedDataTable
